import React, { useState } from 'react';
import { useT } from '../../i18n';

// The NFTs the tracked wallets hold, one row per collection, opening onto one
// row per item: seven Clone X are one line with each token underneath, and
// each carries its own cost, because tokens bought at different times cost
// different amounts.
//
// Value Invested is read off the transaction the NFT arrived in — what the
// wallet paid there, in USD on the day — and links to it. Current Value is the
// collection's floor. Either is a dash, never a zero, when it cannot be told:
// a gift or a transfer paid nothing, and a 1/1 has no floor. Those cells are
// marked as needing a figure, and both can be typed over from the pencil at
// the item's end; clearing a field falls back to the derived figure.

const COLUMNS = [
    { key: 'name', labelKey: 'nft.col.collection', align: 'left' },
    { key: 'quantity', labelKey: 'nft.col.qty', align: 'right' },
    { key: 'cost', labelKey: 'nft.col.invested', align: 'right' },
    { key: 'value', labelKey: 'nft.col.value', align: 'right' },
    { key: 'pricedAt', labelKey: 'nft.col.pricedAt', align: 'right' },
    { key: 'pnl', labelKey: 'nft.col.pnl', align: 'right' },
];

const known = (v) => v !== null && v !== undefined;

/** An item's P/L, or a collection's when every item in it is known. */
const pnlOf = (row) => {
    if (row.items) {
        return row.unknownCost === 0 && row.unknownValue === 0 && known(row.costUsd) && known(row.valueUsd)
            ? row.valueUsd - row.costUsd
            : null;
    }
    return known(row.costUsd) && known(row.valueUsd) ? row.valueUsd - row.costUsd : null;
};

function Thumb({ src, name }) {
    const [broken, setBroken] = useState(false);
    if (!src || broken) {
        return <span className="nft__thumb is-blank" aria-hidden="true">{(name || '?').slice(0, 1)}</span>;
    }
    return (
        <img
            className="nft__thumb"
            src={src}
            alt=""
            width="28"
            height="28"
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            onError={() => setBroken(true)}
        />
    );
}

function PencilIcon() {
    return (
        <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">
            <path d="M11.2 2.3l2.5 2.5-8.4 8.4-3.1.6.6-3.1z" />
            <path d="M9.6 3.9l2.5 2.5" />
        </svg>
    );
}

function LinkIcon() {
    return (
        <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true" focusable="false">
            <path d="M9 2.5h4.5V7" />
            <path d="M13.5 2.5L7.5 8.5" />
            <path d="M11.5 9.5v4h-9v-9h4" />
        </svg>
    );
}

/** A typed amount: '' is "use the derived figure", anything else must be a
 *  non-negative number. Undefined for something that is neither. */
export function parseAmount(text) {
    const trimmed = String(text ?? '').replace(/[$,\s]/g, '');
    if (trimmed === '') return null;
    const n = Number(trimmed);
    return Number.isFinite(n) && n >= 0 ? n : undefined;
}

const draftOf = (item) => ({
    cost: item.costOverrideUsd === null || item.costOverrideUsd === undefined ? '' : String(item.costOverrideUsd),
    value: item.valueOverrideUsd === null || item.valueOverrideUsd === undefined ? '' : String(item.valueOverrideUsd),
});

export default function NftTable({ collections, onSave, busy = false }) {
    const { t, tn, fmt } = useT();
    const [open, setOpen] = useState(() => new Set());
    const [editing, setEditing] = useState(null);
    const [draft, setDraft] = useState({ cost: '', value: '' });
    const [saving, setSaving] = useState(false);

    if (!collections || collections.length === 0) return null;

    const money = (v) => (known(v) ? fmt.number(v) : null);
    const signed = (n) => {
        const s = fmt.number(n);
        return s !== null && n > 0 ? `+${s}` : s;
    };

    const toggleOpen = (key) =>
        setOpen((current) => {
            const next = new Set(current);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });

    const startEdit = (item) => {
        setEditing(item.id);
        setDraft(draftOf(item));
    };

    const cost = parseAmount(draft.cost);
    const value = parseAmount(draft.value);
    const valid = cost !== undefined && value !== undefined;

    const save = async () => {
        // Enter can fire again while the first save is in flight.
        if (!valid || saving) return;
        setSaving(true);
        const ok = await onSave(editing, { costUsd: cost, valueUsd: value });
        setSaving(false);
        if (ok) setEditing(null);
    };

    /** A figure nobody can derive: a dash, marked so it reads as "fill me in"
     *  rather than "nothing". */
    const needs = (label) => (
        <span className="positions__empty nft__needs" title={t('nft.needsInput')} aria-label={t('nft.needsInputAria', { field: label })}>
            —
        </span>
    );

    const input = (field, labelKey, placeholder) => (
        <input
            className={`private__input is-right${parseAmount(draft[field]) === undefined ? ' is-invalid' : ''}`}
            inputMode="decimal"
            value={draft[field]}
            placeholder={placeholder ?? ''}
            aria-label={t(labelKey)}
            onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.value }))}
            onKeyDown={(e) => {
                if (e.key === 'Enter') save();
                if (e.key === 'Escape') setEditing(null);
            }}
        />
    );

    /** The item a row edits: an item row's own, or a one-item collection's. */
    const itemOf = (row) => (row.items ? (row.items.length === 1 ? row.items[0] : null) : row);

    const cell = (row, key, child) => {
        const item = itemOf(row);
        const isEditing = item && editing === item.id;
        switch (key) {
            case 'name': {
                const grouped = !child && row.items?.length > 1;
                const expanded = open.has(row.key);
                const title = child ? (row.name || `#${row.tokenId}`) : row.name;
                const sub = child
                    ? null
                    : grouped
                        ? tn('nft.items', row.items.length)
                        : row.items[0].name && row.items[0].name !== row.name ? row.items[0].name : null;
                return (
                    <span className="nft__ident">
                        {grouped && (
                            <button
                                type="button"
                                className="crypto__expand"
                                aria-expanded={expanded}
                                aria-label={t(expanded ? 'nft.collapse' : 'nft.expand', { name: row.name })}
                                onClick={() => toggleOpen(row.key)}
                            >
                                <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
                            </button>
                        )}
                        <Thumb src={row.imageUrl} name={title} />
                        <span className="nft__names">
                            <span className="crypto__symbol">{title}</span>
                            {sub && <span className="crypto__name">{sub}</span>}
                        </span>
                        {!child && <span className="crypto__kind">{t(`crypto.chains.${row.chain}`)}</span>}
                        {!child && row.wallets > 1 && (
                            <span className="crypto__name">{tn('nft.walletCount', row.wallets)}</span>
                        )}
                    </span>
                );
            }
            case 'quantity': {
                const qty = row.items ? row.items.reduce((s, i) => s + i.quantity, 0) : row.quantity;
                return fmt.number(qty, 0);
            }
            case 'cost': {
                if (isEditing) return input('cost', 'nft.col.invested', money(item.derivedCostUsd));
                const shown = money(row.costUsd);
                const link = item?.txUrl && (
                    <a
                        className="nft__tx"
                        href={item.txUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={t('nft.viewTx')}
                        aria-label={t('nft.viewTx')}
                    >
                        <LinkIcon />
                    </a>
                );
                if (!row.items || item) {
                    return (
                        <>
                            {shown ?? needs(t('nft.col.invested'))}
                            {item?.costOverrideUsd !== null && item?.costOverrideUsd !== undefined && (
                                <span className="nft__edited" title={t('nft.edited')}>*</span>
                            )}
                            {link}
                        </>
                    );
                }
                return (
                    <>
                        {shown}
                        {row.unknownCost > 0 && (
                            <span className="nft__unknown">{tn('nft.unknown', row.unknownCost)}</span>
                        )}
                    </>
                );
            }
            case 'value': {
                if (isEditing) return input('value', 'nft.col.value', money(item.derivedValueUsd));
                const shown = money(row.valueUsd);
                if (!row.items || item) {
                    return (
                        <>
                            {shown ?? needs(t('nft.col.value'))}
                            {item?.valueOverrideUsd !== null && item?.valueOverrideUsd !== undefined && (
                                <span className="nft__edited" title={t('nft.edited')}>*</span>
                            )}
                        </>
                    );
                }
                return (
                    <>
                        {shown}
                        {row.unknownValue > 0 && (
                            <span className="nft__unknown">{tn('nft.unknown', row.unknownValue)}</span>
                        )}
                    </>
                );
            }
            case 'pricedAt':
                if (item) return fmt.date(item.pricedAt);
                return row.floor?.at ? fmt.date(row.floor.at) : null;
            case 'pnl': {
                const pnl = pnlOf(item ?? row);
                return pnl === null ? null : signed(pnl);
            }
            default:
                return null;
        }
    };

    const actions = (row) => {
        const item = itemOf(row);
        if (!item) return null;
        if (editing === item.id) {
            return (
                <div className="private__stack">
                    <button type="button" className="settings__rename_go" onClick={save} disabled={busy || saving || !valid}>
                        {t('common.save')}
                    </button>
                    <button type="button" className="settings__rename_go" onClick={() => setEditing(null)}>
                        {t('common.cancel')}
                    </button>
                </div>
            );
        }
        return (
            <button
                type="button"
                className="private__edit"
                onClick={() => startEdit(item)}
                disabled={busy || editing !== null}
                aria-label={t('nft.edit', { name: item.name || row.name || `#${item.tokenId}` })}
                title={t('nft.edit', { name: item.name || row.name || `#${item.tokenId}` })}
            >
                <PencilIcon />
            </button>
        );
    };

    return (
        <div className="positions__scroll">
            <table className="positions nft__table">
                <thead>
                    <tr>
                        {COLUMNS.map((c) => (
                            <th key={c.key} className={`is-${c.align}`}>{t(c.labelKey)}</th>
                        ))}
                        <th className="is-right"><span className="nft__sr">{t('nft.col.actions')}</span></th>
                    </tr>
                </thead>
                <tbody>
                    {collections.flatMap((collection) => {
                        const children = collection.items.length > 1 && open.has(collection.key)
                            ? collection.items
                            : [];
                        return [collection, ...children].map((row, i) => {
                            const child = i > 0;
                            const pnl = pnlOf(itemOf(row) ?? row);
                            return (
                                <tr key={child ? `item-${row.id}` : collection.key} className={child ? 'crypto__member' : undefined}>
                                    {COLUMNS.map((c) => {
                                        const content = cell(row, c.key, child);
                                        const tone = c.key === 'pnl' && pnl !== null && pnl !== 0
                                            ? (pnl < 0 ? ' is-negative' : ' is-positive')
                                            : '';
                                        return (
                                            <td key={c.key} className={`is-${c.align}${tone}`}>
                                                {content ?? <span className="positions__empty">—</span>}
                                            </td>
                                        );
                                    })}
                                    <td className="is-right private__actions">{actions(row)}</td>
                                </tr>
                            );
                        });
                    })}
                </tbody>
            </table>
        </div>
    );
}
